import { useMemo } from "react";
import { LoaderCircle } from "lucide-react";

import { cn } from "@/lib/utils";

import type {
  SceneBlock,
  SceneBrandkit,
  ScenePlan,
  SocialPostGenerationTaskDetail,
} from "../types";
import { getBlockStatus } from "../utils";

export function SceneLayerPanel({
  plan,
  linkedAssets,
  brandkit,
  selectedBlockId,
  onSelectBlock,
}: {
  plan: ScenePlan;
  linkedAssets: SocialPostGenerationTaskDetail["linkedAssets"];
  brandkit: SceneBrandkit;
  selectedBlockId: string | null;
  onSelectBlock: (blockId: string) => void;
}) {
  const layers = useMemo(() => {
    const rows: Array<{ block: SceneBlock; isChild: boolean; parentId?: string }> = [];
    const topLevel = plan.scene.blocks.toSorted((a, b) => b.zIndex - a.zIndex);
    for (const block of topLevel) {
      rows.push({ block, isChild: false });
      if (block.type === "group") {
        const children = block.children.toSorted((a, b) => b.zIndex - a.zIndex);
        for (const child of children) {
          rows.push({ block: child, isChild: true, parentId: block.id });
        }
      }
    }

    return rows;
  }, [plan.scene.blocks]);
  const imageAssetByBlockId = useMemo(
    () => new Map(linkedAssets.map((asset) => [asset.blockId, asset] as const)),
    [linkedAssets],
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Layers
        </div>
        <div className="text-xs leading-5 text-muted-foreground">
          {layers.length} selectable blocks ordered from front to back.
        </div>
      </div>
      <div className="space-y-2">
        {layers.map((layerRow) => {
          const layer = layerRow.block;
          const status = getBlockStatus(layer, imageAssetByBlockId, brandkit.logo);
          const isSelected = selectedBlockId === layer.id;
          return (
            <button
              key={layerRow.parentId ? `${layerRow.parentId}:${layer.id}` : layer.id}
              type="button"
              onClick={() => onSelectBlock(layer.id)}
              className={cn(
                "relative flex w-full items-center justify-between overflow-hidden rounded-xl border px-3 py-2.5 text-left text-xs transition-all hover:-translate-y-0.5 hover:shadow-sm",
                isSelected
                  ? "border-foreground/25 bg-muted/60 shadow-sm"
                  : "border-border bg-background hover:border-foreground/20",
                layerRow.isChild && "ml-3 w-[calc(100%-0.75rem)]",
              )}
            >
              {isSelected ? (
                <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-foreground" />
              ) : null}
              <div className="min-w-0">
                <div className="truncate font-medium">
                  {layerRow.isChild ? "↳ " : ""}
                  {layer.id}
                </div>
                <div className="font-mono text-[11px] text-muted-foreground">{layer.type}</div>
              </div>
              {status === "pending" ? (
                <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
